import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import UserLayout from '../../components/Layout/UserLayout';
import { Card, StatCard, Badge, Button, Select, Spinner, EmptyState, PageHeader } from '../../components/UI';
import { dashboardAPI, leadsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import {
  HiOutlineUsers,
  HiOutlinePhone,
  HiOutlineTrendingUp,
  HiOutlineCalendar,
  HiOutlineRefresh,
  HiOutlineArrowRight,
  HiOutlineClock,
} from 'react-icons/hi';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

const formatDuration = (seconds = 0) => {
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [range, setRange] = useState('week');

  const {
    data: stats,
    isLoading: statsLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['userDashboardStats', range],
    queryFn: async () => {
      const response = await dashboardAPI.getStats({ range });
      return response.data?.data || {};
    },
  });

  const { data: recentLeads, isLoading: leadsLoading } = useQuery({
    queryKey: ['userRecentLeads'],
    queryFn: async () => {
      const response = await leadsAPI.getMy({ limit: 5 });
      return response.data?.data || [];
    },
  });

  const handleRefresh = async () => {
    await refetch();
    toast.success('Dashboard refreshed');
  };

  const calls = stats?.calls || { total: 0, connected: 0, notConnected: 0 };
  const analytics = stats?.callAnalytics || {};
  const stages = stats?.leads?.stages || [];
  const hourly = (stats?.hourlyCalls || []).map((h) => ({ ...h, notConnected: h.total - h.connected }));
  const hasHourlyData = hourly.some((h) => h.total > 0);

  return (
    <UserLayout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          title={`Welcome back${user?.firstName ? `, ${user.firstName}` : ''}`}
          description="Here's an overview of your work."
          action={
            <>
              <Select
                options={RANGE_OPTIONS}
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="w-36"
              />
              <Button
                variant="outline"
                size="sm"
                icon={<HiOutlineRefresh className="w-4 h-4" />}
                onClick={handleRefresh}
                loading={isFetching}
              >
                Refresh
              </Button>
            </>
          }
        />

        {statsLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <StatCard
                title="My Leads"
                value={stats?.allTimeTotalLeads || 0}
                icon={<HiOutlineUsers className="w-6 h-6 text-white" />}
                color="primary"
              />
              <StatCard
                title="Calls Today"
                value={stats?.callsToday || 0}
                icon={<HiOutlinePhone className="w-6 h-6 text-white" />}
                color="info"
              />
              <StatCard
                title="Follow-ups Due Today"
                value={stats?.followUpsDueToday || 0}
                icon={<HiOutlineCalendar className="w-6 h-6 text-white" />}
                color="warning"
              />
              <StatCard
                title="Converted"
                value={stats?.convertedLeads || 0}
                icon={<HiOutlineTrendingUp className="w-6 h-6 text-white" />}
                color="success"
              />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 lg:gap-6">
              {/* Call activity */}
              <Card padding="p-4 sm:p-6" className="xl:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-secondary-900">Today's Call Activity</h3>
                  <span className="text-xs text-secondary-500">9 AM – 9 PM</span>
                </div>
                {hasHourlyData ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hourly} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <Tooltip />
                        <Bar dataKey="connected" name="Connected" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="notConnected" name="Not Connected" stackId="a" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <EmptyState
                    icon={<HiOutlinePhone className="w-8 h-8 text-secondary-400" />}
                    title="No calls yet today"
                    description="Calls you make from the TeleValley app will show up here"
                  />
                )}
              </Card>

              {/* Call summary for the selected range */}
              <Card padding="p-4 sm:p-6">
                <h3 className="font-semibold text-secondary-900 mb-4">
                  Call Summary <span className="text-secondary-400 font-normal">· {RANGE_OPTIONS.find((r) => r.value === range)?.label}</span>
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Total Calls', value: calls.total },
                    { label: 'Connected', value: calls.connected },
                    { label: 'Not Connected', value: calls.notConnected },
                    { label: 'Talk Time', value: formatDuration(analytics.totalTalkTime) },
                    { label: 'Avg. Call Duration', value: formatDuration(analytics.averageDuration) },
                    { label: 'Pending Follow-ups', value: stats?.pendingFollowUps || 0 },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between text-sm">
                      <span className="text-secondary-500">{row.label}</span>
                      <span className="font-semibold text-secondary-900">{row.value}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>

            {/* Lead stages */}
            <Card padding="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-secondary-900">Lead Status Overview</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/app/leads')}>
                  View All
                  <HiOutlineArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {stages.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {stages.map((stage) => (
                    <div
                      key={stage.key}
                      className="p-3 rounded-xl border border-secondary-200/70 bg-white/60 text-center"
                    >
                      <p className="text-2xl font-bold" style={{ color: stage.color }}>
                        {stage.leadsCount}
                      </p>
                      <p className="text-xs text-secondary-600 mt-0.5 truncate" title={stage.name}>{stage.name}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={<HiOutlineUsers className="w-8 h-8 text-secondary-400" />}
                  title="No leads in this period"
                  description="Leads assigned to you will be grouped by stage here"
                />
              )}
            </Card>

            {/* Recent leads */}
            <Card padding="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-secondary-900">My Recent Leads</h3>
                <Button variant="ghost" size="sm" onClick={() => navigate('/app/leads')}>
                  View All
                  <HiOutlineArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              {leadsLoading ? (
                <div className="flex justify-center py-8">
                  <Spinner size="lg" />
                </div>
              ) : recentLeads?.length > 0 ? (
                <div className="space-y-2">
                  {recentLeads.slice(0, 5).map((lead) => (
                    <div
                      key={lead._id}
                      className="flex items-center justify-between p-3 rounded-xl bg-secondary-50 hover:bg-secondary-100 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-600 font-medium text-sm">
                            {lead.name?.charAt(0)?.toUpperCase() || 'L'}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-secondary-900 truncate">{lead.name}</p>
                          <p className="text-sm text-secondary-500">{lead.phone}</p>
                        </div>
                      </div>
                      <Badge variant={lead.status} size="sm">
                        {lead.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={<HiOutlineClock className="w-8 h-8 text-secondary-400" />}
                  title="No leads assigned yet"
                  description="Leads will appear here when assigned to you"
                />
              )}
            </Card>
          </>
        )}
      </div>
    </UserLayout>
  );
};

export default UserDashboard;
